import React, { useMemo } from 'react';
import Container from 'src/components/shared/Container';
import Seo from 'src/components/Seo';
import PlacementOptOutSection from 'src/components/support/PlacementOptOutSection';
import HelpdeskSection from 'src/components/support/HelpdeskSection';
import SupportQuestionsSection from 'src/components/support/SupportQuestionsSection';
import MyTicketsSection from 'src/components/support/MyTicketsSection';
import SupportModal from 'src/components/support/SupportModal';
import { Breadcrumb } from 'flowbite-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import seoConfig from '../seo.json';
import { sendTrackingEvent } from 'src/utils/gtm';
import { HiOutlineLightningBolt } from 'react-icons/hi';
import { DRIVE_FONT_STYLE, useDriveFont } from 'src/components/placementDrives/shared';

const Support = () => {
    useDriveFont();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const seoInfo = seoConfig['support'] || { title: 'Support | LevelUp', description: 'Support page for LevelUp' };

    // URL as source of truth: tickets=ticketlisting → Support Tickets tab; otherwise Help
    const ticketsListingParam = searchParams.get('tickets') === 'ticketlisting';
    const activeTab: 'help' | 'tickets' = ticketsListingParam ? 'tickets' : 'help';

    // Show modal when: (1) modal=search/fallback, (2) ticket details (step=ticketdetails&ticketId=...), or (3) category+subcategory (Help flow)
    const stepTicketDetails = searchParams.get('step') === 'ticketdetails';
    const urlTicketId = searchParams.get('ticketId');
    const showTicketDetailsInModal = !!(stepTicketDetails && urlTicketId);
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');
    const showSupportModal = useMemo(() => {
        const modal = searchParams.get('modal');
        if (modal === 'search' || modal === 'fallback') return true;
        if (showTicketDetailsInModal) return true;
        // Help flow: category+subcategory opens modal (handled by SupportQuestionsSection opening modal via URL)
        return !!(categoryParam && subcategoryParam);
    }, [searchParams, showTicketDetailsInModal, categoryParam, subcategoryParam]);

    const handleCloseSupportModal = () => {
        const params = new URLSearchParams(location.search);
        params.delete('modal');
        params.delete('step');
        params.delete('ticketId');
        params.delete('category');
        params.delete('subcategory');
        params.delete('questionId');
        const qs = params.toString();
        navigate(qs ? `/support?${qs}` : '/support', { replace: true });
    };

    return (
        <div className="pb-[200px] md:pb-[180px]">
            <Seo title={seoInfo.title} description={seoInfo.description} />
            <Container className="dp-fade w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%]" style={DRIVE_FONT_STYLE}>
                <div className="md:mt-6 mb-3">
                    <Breadcrumb aria-label="support-breadcrumb">
                        <Breadcrumb.Item
                            onClick={() => navigate('/')}
                            className="cursor-pointer"
                        >
                            <p className="font-inter text-sm font-medium dark:text-[#EBEBF0] text-[#555770]">
                                Home
                            </p>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <p className="font-inter text-sm font-medium dark:text-[#EBEBF0] text-[#555770]">
                                Support
                            </p>
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </div>

                <div className="mb-6">
                    <h1 className="text-[28px] leading-[34px] md:text-[34px] md:leading-[40px] font-[800] tracking-[-0.03em] text-[#111E2C] dark:text-white">Support</h1>
                    <p className="mt-1.5 text-[15px] text-[#555770] dark:text-[#C7C9D9]">Find answers, raise a ticket and track your requests.</p>
                </div>

                {/* Top Sections - Always Visible */}
                <div className="flex flex-col gap-6 mb-6">
                    {/* Placement Opt-out - Always at top */}
                    <PlacementOptOutSection />

                    {/* Helpdesk - Second at top */}
                    <HelpdeskSection />
                </div>

                {/* Floating banner: New Support Feature in Testing - yellow/pink, font-inter for consistency */}
                <div className="dp-rise mb-5 flex items-start gap-3 rounded-[14px] border border-[#F3E6B3] bg-[#FFFAE8] px-4 py-3.5 text-[14px] leading-[22px] text-[#6B5200] dark:border-[#5A4A12] dark:bg-[#3A3214] dark:text-[#F5E6A8]">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#B7791F] shadow-[0_1px_2px_rgba(20,20,60,0.08)] dark:bg-[#2A2410]">
                        <HiOutlineLightningBolt className="h-[18px] w-[18px]" />
                    </span>
                    <div>
                        <p className="font-[700] text-[#4F3C00] dark:text-[#FFF1C2]">New support feature in testing</p>
                        <p className="mt-0.5">
                            We&apos;re currently testing this feature to improve your support experience. Some information may change while we refine it and review for accuracy.
                        </p>
                    </div>
                </div>

                {/* Tabs Section - URL-driven: tickets=ticketlisting shows Support Tickets */}
                <div className="mb-6">
                    <div className="inline-flex w-full rounded-full bg-[#ECECF3] p-1 dark:bg-[#1F2A37] md:w-auto">
                        <button
                            onClick={() => {
                                sendTrackingEvent({
                                    event: 'support_tab',
                                    tab_name: 'Help',
                                    eventLabel: location.pathname,
                                });
                                const next = new URLSearchParams(searchParams);
                                next.delete('tickets');
                                next.delete('tab');
                                next.delete('step');
                                next.delete('ticketId');
                                const qs = next.toString();
                                navigate(qs ? `/support?${qs}` : '/support', { replace: true });
                            }}
                            className={`flex-1 md:flex-none md:min-w-[150px] rounded-full px-4 md:px-6 py-2.5 md:py-2 text-[14px] outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8F89F5] font-[600] transition-all duration-200 ${activeTab === 'help'
                                ? 'bg-white text-[#3730A3] shadow-[0_2px_8px_rgba(20,20,60,0.10)] dark:bg-[#18222D] dark:text-[#8F89F5]'
                                : 'text-[#555770] hover:text-[#111E2C] dark:text-[#B0B8C4] dark:hover:text-white'
                                }`}
                        >
                            Help
                        </button>
                        <button
                            onClick={() => {
                                sendTrackingEvent({
                                    event: 'support_tab',
                                    tab_name: 'Support Tickets',
                                    eventLabel: location.pathname,
                                });
                                const next = new URLSearchParams(searchParams);
                                next.set('tickets', 'ticketlisting');
                                // Default to unresolved when switching to ticket listing
                                if (!next.has('tab')) next.set('tab', 'unresolved');
                                next.delete('step');
                                next.delete('ticketId');
                                navigate(`/support?${next.toString()}`, { replace: true });
                            }}
                            className={`flex-1 md:flex-none md:min-w-[150px] rounded-full px-4 md:px-6 py-2.5 md:py-2 text-[14px] outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8F89F5] font-[600] transition-all duration-200 ${activeTab === 'tickets'
                                ? 'bg-white text-[#3730A3] shadow-[0_2px_8px_rgba(20,20,60,0.10)] dark:bg-[#18222D] dark:text-[#8F89F5]'
                                : 'text-[#555770] hover:text-[#111E2C] dark:text-[#B0B8C4] dark:hover:text-white'
                                }`}
                        >
                            Support Tickets
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div key={activeTab} className="dp-fade mt-4">
                    {activeTab === 'help' ? (
                        <SupportQuestionsSection onOpenCreateTicket={() => navigate('/support?modal=fallback')} />
                    ) : (
                        <MyTicketsSection />
                    )}
                </div>

            </Container>

            {/* Support Modal - 3 versions: fallback (ticket form), search (by batch), questions (category+subcategory).
                Kept outside the animated Container: a transformed ancestor turns position:fixed into position:relative to it,
                which made the backdrop and panel jump when the page opened with the modal params in the URL. */}
            <SupportModal show={showSupportModal} onClose={handleCloseSupportModal} />
        </div>
    );
};

export default Support;
